import React from "react";
import { X } from "lucide-react";
import FileIcon from "../FileExplorer/FileIcon";
import { CLASSES, CLOSE_ICON_SIZE } from "../../constants";

/**
 * Individual tab component
 * @param {Object} props - Component props
 * @param {Object} props.tab - Tab object with path, name, content
 * @param {boolean} props.isActive - Whether this tab is active
 * @param {boolean} props.isModified - Whether this tab has unsaved changes
 * @param {Function} props.onClick - Click handler for switching to tab
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onMiddleClick - Middle click handler for closing
 * @returns {JSX.Element} The tab component
 */
const Tab = ({
  tab,
  isActive,
  isModified,
  onClick,
  onClose,
  onMiddleClick,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(tab.path);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (onClose) {
      onClose(tab.path, e);
    }
  };

  const handleMouseDown = (e) => {
    // Middle mouse button click to close tab
    if (e.button === 1 && onMiddleClick) {
      e.preventDefault();
      onMiddleClick(tab.path, e);
    }
  };

  return (
    <div
      className={`
        ${CLASSES.tab}
        ${isActive ? CLASSES.tabActive : CLASSES.tabInactive}
      `}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      title={tab.path}
    >
      {/* File icon */}
      <span className="mr-2">
        <FileIcon fileName={tab.name} />
      </span>

      {/* File name */}
      <span className="text-xs text-neutral-800 dark:text-neutral-200 font-medium mr-2 truncate max-w-[120px]">
        {tab.name}
      </span>

      {/* Modified indicator */}
      {isModified && (
        <span className="text-xs text-orange-500 dark:text-orange-400 mr-1">
          ●
        </span>
      )}

      {/* Close button */}
      <button
        className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleClose}
        title="Close tab"
      >
        <X size={CLOSE_ICON_SIZE} />
      </button>
    </div>
  );
};

export default Tab;
